import moment from 'moment';
import { amber, blue, green, grey, indigo, red, teal } from '@mui/material/colors';
import { LenderApprovalTermsBasis } from '@trnsact/trnsact-shared-types';
import { formatCurrency } from 'utils';
import { formatPercentValue } from '../lib';
import {
  ApprovalTermsSummaryCardConfig,
  AverageRateTierConfig,
  ConversionTableColumnConfig,
  ConversionTableColumnKey,
  ConversionTableSorting,
  FunnelConversionConfig,
  FunnelStageConfig,
  LenderPerformanceFilterFormValues,
  RatioCardConfig,
  ReportTypes,
  TermsComparisonColumnConfig,
  TermsComparisonColumnKey,
} from '../types';

export const REPORT_OPTIONS = [
  { label: 'Application Reports', value: ReportTypes.ApplicationReports },
  { label: 'Lender Performance', value: ReportTypes.LenderPerformance },
];

export const LENDER_PERFORMANCE_DEFAULT_FILTERS: LenderPerformanceFilterFormValues = {
  from: moment().startOf('year'),
  to: moment().endOf('year'),
  lenders: [],
  salesReps: [],
  locations: [],
};

export const APPROVAL_TERMS_BASIS_DEFAULT = LenderApprovalTermsBasis.AllApprovals;

export const RATIO_CARDS: RatioCardConfig[] = [
  {
    key: 'lookToBook',
    title: 'Look-to-Book',
    description: 'Percentage of submissions that become funded deals',
    format: value => formatPercentValue(value),
  },
  {
    key: 'fundRate',
    title: 'Fund Rate',
    description: 'Percentage of approvals that receive funding',
    format: value => formatPercentValue(value),
  },
  {
    key: 'responseRate',
    title: 'Response Rate',
    description: 'Percentage of submissions that received a lender decision',
    format: value => formatPercentValue(value),
  },
  {
    key: 'fundedVolume',
    title: 'Funded Volume',
    description: 'Total funded amount during the selected period',
    format: value => formatCurrency(value, true),
  },
];

export const FUNNEL_STAGES: FunnelStageConfig[] = [
  { key: 'applicationReceived', label: 'Application Received', color: indigo[500], ratePrefix: '' },
  { key: 'submitted', label: 'Submitted', color: blue[700], ratePrefix: 'of received' },
  { key: 'decisioned', label: 'Decisioned', color: blue[400], ratePrefix: 'of submitted' },
  { key: 'approved', label: 'Approved', color: teal[500], ratePrefix: 'of submitted' },
  { key: 'funded', label: 'Funded', color: green[500], ratePrefix: 'of submitted' },
];

export const FUNNEL_CONVERSIONS: FunnelConversionConfig[] = [
  { key: 'submissionRate', label: 'submitted' },
  { key: 'responseRate', label: 'response' },
  { key: 'approvalRate', label: 'approval' },
  { key: 'fundRate', label: 'fund rate' },
];

export const FUNNEL_MAX_BAR_HEIGHT = 200;
export const FUNNEL_MIN_BAR_RATIO = 0.4;

export const CONVERSION_TABLE_COLUMNS: ConversionTableColumnConfig[] = [
  { key: ConversionTableColumnKey.LenderName, label: 'Lender', align: 'left' },
  { key: ConversionTableColumnKey.Sent, label: 'Sent', align: 'right' },
  { key: ConversionTableColumnKey.Approved, label: 'Approved', align: 'right' },
  { key: ConversionTableColumnKey.ApprovalPct, label: 'Approval %', align: 'right', percent: true, colored: true },
  { key: ConversionTableColumnKey.Funded, label: 'Funded', align: 'right' },
  { key: ConversionTableColumnKey.FundPct, label: 'Fund (%)', align: 'right', percent: true, colored: true },
  { key: ConversionTableColumnKey.LookToBook, label: 'Look-to-Book', align: 'right', percent: true, colored: true },
  { key: ConversionTableColumnKey.FundedAmount, label: 'Funded ($)', align: 'right', currency: true },
];

export const CONVERSION_TABLE_DEFAULT_SORTING: ConversionTableSorting = {
  key: ConversionTableColumnKey.Sent,
  value: 'desc',
};

export const LOOK_TO_BOOK_CHART_HEIGHT = 400;

export const APPROVAL_TERMS_SUMMARY_CARDS: ApprovalTermsSummaryCardConfig[] = [
  {
    key: 'approvedRate',
    title: 'Avg approved rate',
    formatValue: value => formatPercentValue(value),
    formatRange: (min, max) => `Range: ${formatPercentValue(min)} – ${formatPercentValue(max)}`,
  },
  {
    key: 'approvedTerm',
    title: 'Avg approved term',
    formatValue: value => `${Math.round(value)} mo`,
    formatRange: (min, max) => `Range: ${Math.round(min)} – ${Math.round(max)} mo`,
  },
  {
    key: 'docFee',
    title: 'Avg doc/admin fee',
    formatValue: value => formatCurrency(value, true),
    formatRange: (min, max) => `Range: ${formatCurrency(min, true)} – ${formatCurrency(max, true)}`,
  },
];

export const TERMS_COMPARISON_TABLE_COLUMNS: TermsComparisonColumnConfig[] = [
  {
    key: TermsComparisonColumnKey.LenderName,
    label: 'Lender',
    align: 'left',
    sortKey: TermsComparisonColumnKey.LenderName,
  },
  {
    key: TermsComparisonColumnKey.FundedAmount,
    label: 'Funded ($)',
    align: 'right',
    sortKey: TermsComparisonColumnKey.FundedAmount,
  },
  {
    key: TermsComparisonColumnKey.AvgRate,
    label: 'Avg rate',
    align: 'right',
    sortKey: TermsComparisonColumnKey.AvgRate,
  },
  { key: TermsComparisonColumnKey.RateRange, label: 'Rate range', align: 'right' },
  {
    key: TermsComparisonColumnKey.AvgTerm,
    label: 'Avg term (mo)',
    align: 'right',
    sortKey: TermsComparisonColumnKey.AvgTerm,
  },
  { key: TermsComparisonColumnKey.TermOptions, label: 'Term options', align: 'left' },
  { key: TermsComparisonColumnKey.DocFee, label: 'Doc fee', align: 'right', sortKey: TermsComparisonColumnKey.DocFee },
];

export const TERMS_COMPARISON_DEFAULT_SORTING: ConversionTableSorting = {
  key: TermsComparisonColumnKey.FundedAmount,
  value: 'desc',
};

export const AVERAGE_RATE_TIERS: AverageRateTierConfig[] = [
  { label: 'Competitive (<8%)', color: teal[300], max: 8 },
  { label: 'Mid (8-10%)', color: amber[300], max: 10 },
  { label: 'High (>10%)', color: red[300], max: Infinity },
];

export const TERM_DISTRIBUTION_COLORS = [blue[100], blue[200], blue[300], green[200], green[300], green[400]];

// Continuous color scale for the Look-to-Book by lender heatmap (0% -> 100%).
export const LOOK_TO_BOOK_HEATMAP_MIN_COLOR = 'rgb(227, 242, 253)'; // blue[50]
export const LOOK_TO_BOOK_HEATMAP_MAX_COLOR = 'rgb(13, 71, 161)'; // blue[900]
export const LOOK_TO_BOOK_HEATMAP_NO_DATA_COLOR = grey[100];
export const LOOK_TO_BOOK_HEATMAP_MAX_HEIGHT = 440;
